import type { FernyActionKind, FernyActionResult } from "~/services/aiOrchestrator";
import type { FernieAction } from "~/store/fernieActionSlice";

import resolveNavigationTarget, {
	candidateLabel,
	destinationLabel,
	getDestinationIndex,
	isExternalPath,
	isKnownDestinationPath
} from "~/helpers/aiChat/navigationDestinations";
import parseReportDeepLink, { REPORTS_PATH } from "~/helpers/aiChat/reportDeepLink";
import { isLotRequest, lotSkipReason } from "~/helpers/aiChat/requestedDestination";
import { parseScheduleJobTarget, SCHEDULE_PATH } from "~/helpers/aiChat/scheduleJobDeepLink";
import logger from "~/helpers/logger";
import { getWalkthrough } from "~/walkthroughs/registry";

export interface SkippedAction {
	reason: string;
	type: string;
}

export interface NavCandidate {
	label: string;
	path: string;
}

export interface NavConfirmation {
	candidates: NavCandidate[];
	query: string;
}

export interface ProcessedActions {
	confirmations: NavConfirmation[];
	dispatched: FernieAction[];
	skipped: SkippedAction[];
}

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const isFernyActionResult = (value: unknown): value is FernyActionResult =>
	typeof value === "object" &&
	value !== null &&
	typeof (value as FernyActionResult).kind === "string" &&
	typeof (value as FernyActionResult).requestedDestination === "string" &&
	typeof (value as FernyActionResult).deepLink === "string";

type Validated = { action: FernieAction } | { confirmation: NavConfirmation } | { skipped: SkippedAction };

type CanOpen = (path: string) => boolean;

const skip = (type: string, reason: string): { skipped: SkippedAction } => ({ skipped: { reason, type } });

const dispatch = (action: FernieAction): { action: FernieAction } => ({ action });

const confirm = (confirmation: NavConfirmation): { confirmation: NavConfirmation } => ({ confirmation });

const navigateTo = (action: FernyActionResult, path: string): { action: FernieAction } =>
	dispatch({
		path,
		reachedDestination: action.reachedDestination,
		requestedDestination: action.requestedDestination,
		type: "navigateTo"
	});

const LEGACY_PATHS: Array<[RegExp, string]> = [
	[/^\/admin(?=[/?#]|$)/, "/settings"],
	[/^\/integrations(?=[/?#]|$)/, "/settings/integrations"],
	[/^\/administration\/data-management(?=[?#]|$)/, "/settings/data-management"]
];

const toCurrentPath = (deepLink: string): string => {
	const rewritten = LEGACY_PATHS.reduce((path, [legacy, current]) => path.replace(legacy, current), deepLink);
	return isKnownDestinationPath(rewritten) ? rewritten : deepLink;
};

const resolvePath = (action: FernyActionResult, type: string, canOpen: CanOpen): Validated => {
	const deepLink = toCurrentPath(action.deepLink.trim());
	if (deepLink && !isExternalPath(deepLink) && isKnownDestinationPath(deepLink) && canOpen(deepLink))
		return navigateTo(action, deepLink);
	if (isLotRequest(action.requestedDestination)) return skip(type, lotSkipReason(action.requestedDestination));
	if (isNonEmptyString(action.requestedDestination)) {
		const openable = getDestinationIndex().filter((destination) => canOpen(destination.path));
		const match = resolveNavigationTarget(action.requestedDestination, openable);
		if (match.kind === "exact" && isExternalPath(match.destination.path))
			return confirm({
				candidates: [{ label: destinationLabel(match.destination), path: match.destination.path }],
				query: action.requestedDestination
			});
		if (match.kind === "exact") return navigateTo(action, match.destination.path);
		if (match.kind === "list")
			return confirm({
				candidates: match.candidates.map((candidate) => ({
					label: candidateLabel(candidate, match.candidates),
					path: candidate.path
				})),
				query: match.query
			});
		return skip(type, `find a destination matching "${action.requestedDestination}"`);
	}
	return skip(type, "navigate without a destination");
};

const validateNavigate = (action: FernyActionResult, canOpen: CanOpen): Validated => {
	const reportTarget = parseReportDeepLink(action.deepLink);
	if (reportTarget.kind === "report")
		return dispatch({
			path: REPORTS_PATH,
			reportName: reportTarget.reportName,
			requestedDestination: action.requestedDestination,
			type: "openReport"
		});
	return resolvePath(action, "navigateTo", canOpen);
};

const validateOpenPanel = (action: FernyActionResult): Validated => {
	const scheduleTarget = parseScheduleJobTarget(action.deepLink);
	if (scheduleTarget.kind === "job")
		return dispatch({
			jobNumber: scheduleTarget.jobNumber,
			path: SCHEDULE_PATH,
			requestedDestination: action.requestedDestination,
			type: "openSchedulePanel"
		});
	if (isLotRequest(action.requestedDestination))
		return skip("openSchedulePanel", lotSkipReason(action.requestedDestination));
	if (scheduleTarget.kind === "unreadable") return skip("openSchedulePanel", "tell which job to open on the schedule");
	const panel = isNonEmptyString(action.deepLink) ? action.deepLink.trim() : action.requestedDestination.trim();
	if (!isNonEmptyString(panel)) return skip("openPanel", "open a panel without a target");
	return dispatch({ panel, type: "openPanel" });
};

const validateStartWalkthrough = (action: FernyActionResult): Validated => {
	const tourId = action.walkthroughId?.trim();
	if (!tourId) return skip("startWalkthrough", "start a walkthrough without knowing which one");
	if (!getWalkthrough(tourId)) return skip("startWalkthrough", `start a walkthrough called "${tourId}"`);
	return dispatch({ tourId, type: "startWalkthrough" });
};

const validateAction = (action: FernyActionResult, canOpen: CanOpen): Validated => {
	switch (action.kind) {
		case "NAVIGATE_TO":
			return validateNavigate(action, canOpen);
		case "APPLY_GRID_FILTER":
			return resolvePath(action, "applyGridFilter", canOpen);
		case "OPEN_PANEL":
			return validateOpenPanel(action);
		case "START_WALKTHROUGH":
			return validateStartWalkthrough(action);
		default:
			return skip(action.kind, `understand the action "${action.kind as FernyActionKind}"`);
	}
};

const processFernieActions = (actions: FernyActionResult[], canOpen: CanOpen): ProcessedActions => {
	const result: ProcessedActions = { confirmations: [], dispatched: [], skipped: [] };
	for (const action of actions) {
		if (!isFernyActionResult(action)) {
			logger.warn("Malformed Fernie navigation action", { action });
			result.skipped.push({ reason: "carry out a malformed action", type: "unknown" });
			continue;
		}
		const validated = validateAction(action, canOpen);
		if ("action" in validated) result.dispatched.push(validated.action);
		else if ("confirmation" in validated) result.confirmations.push(validated.confirmation);
		else result.skipped.push(validated.skipped);
	}
	return result;
};

export { processFernieActions };
export default processFernieActions;
